import { useEffect, useRef, useState } from "react"
import styles from "./CursedAlert.module.css"
import { translations } from "../../translations"
import { useTranslationUpdate } from "../../hooks/useTranslationUpdate"
import win from "../Window/Window.module.css"
import alertStyles from "../Window/WindowAlert.module.css"

type Props = {
  onClose: () => void
}

export default function CursedAlert({ onClose }: Props) {
  useTranslationUpdate()
  
  const [position, setPosition] = useState({ x: window.innerWidth / 2 - 210, y: window.innerHeight / 2 - 150 })
  const [isTouchDevice, setIsTouchDevice] = useState(false)
  const alertRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0)
  }, [])

  useEffect(() => {
    if (isTouchDevice) return

    const handleMouseMove = (e: MouseEvent) => {
      if (!closeButtonRef.current || !alertRef.current) return

      const buttonRect = closeButtonRef.current.getBoundingClientRect()
      const mouseX = e.clientX
      const mouseY = e.clientY

      const distance = Math.sqrt(
        Math.pow(mouseX - (buttonRect.left + buttonRect.width / 2), 2) +
        Math.pow(mouseY - (buttonRect.top + buttonRect.height / 2), 2)
      )

      if (distance < 100) {
        const alertRect = alertRef.current.getBoundingClientRect()
        
        const maxX = window.innerWidth - alertRect.width - 20
        const maxY = window.innerHeight - alertRect.height - 20
        
        let newX = Math.random() * maxX
        let newY = Math.random() * maxY
        
        while (Math.abs(newX - position.x) < 200 && Math.abs(newY - position.y) < 200) {
          newX = Math.random() * maxX
          newY = Math.random() * maxY
        }

        setPosition({ x: newX, y: newY })
      }
    }

    window.addEventListener("mousemove", handleMouseMove)
    return () => window.removeEventListener("mousemove", handleMouseMove)
  }, [position, isTouchDevice])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose()
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [onClose])

  return (
    <div className={alertStyles.overlay}>
      <div
        ref={alertRef}
        className={`${win.window} ${alertStyles.alert} ${styles.cursedAlert}`}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          width: '500px',
          height: '400px',
        }}
        role="dialog"
        aria-modal="true"
      >
        <div className={win.header}>
          <div className={win.buttons}>
            <span 
              ref={closeButtonRef}
              className={win.red} 
              onClick={isTouchDevice ? onClose : undefined}
            >
              <svg className={win.icon} width="10" height="10" viewBox="0 0 10 10" aria-hidden>
                <path d="M1 1 L9 9 M9 1 L1 9" stroke="#6C130D" strokeWidth="2" />
              </svg>
            </span>
          </div>
          <span className={win.title}>{translations('cursed_alert.title')}</span>
        </div>

        <div className={`${win.body} ${alertStyles.body} ${styles.body}`}>
          <h2 className={styles.mainTitle}>{translations('cursed_alert.system_compromised')}</h2>
          
          <img src="/images/cat_found.png" alt="Cat detected" className={styles.catImage} />
          
          <p className={styles.message}>
            {translations('cursed_alert.virus_detected')}
          </p>
          
          <p className={styles.hint}>
            {translations('cursed_alert.hint')}
          </p>
        </div>
      </div>
    </div>
  )
}